import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeContext = createContext(null);

export const STORAGE_KEY = 'theme';
const DARK = 'dark';
const LIGHT = 'light';

// Mirrors the inline snippet in public/index.html, which applies the same
// choice before first paint. Keep the two in step.
export function resolveInitialTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === DARK || saved === LIGHT) return saved;
  } catch { /* private mode — fall through to the system preference */ }
  return typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-color-scheme: light)').matches ? LIGHT : DARK;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(resolveInitialTheme);
  // Whether the current theme came from an explicit choice or the OS.
  const [isExplicit, setIsExplicit] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === DARK || localStorage.getItem(STORAGE_KEY) === LIGHT; }
    catch { return false; }
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    // Keep the mobile browser chrome in step with the page.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === LIGHT ? '#ffffff' : '#080b12');
  }, [theme]);

  // Follow the OS while the user has not picked a side themselves.
  useEffect(() => {
    if (isExplicit || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = (e) => setThemeState(e.matches ? LIGHT : DARK);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [isExplicit]);

  const setTheme = useCallback((next) => {
    setThemeState(next === LIGHT ? LIGHT : DARK);
    setIsExplicit(true);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* not fatal */ }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === DARK ? LIGHT : DARK);
  }, [theme, setTheme]);

  // Hand back to the OS preference.
  const useSystemTheme = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* not fatal */ }
    setIsExplicit(false);
    setThemeState(window.matchMedia?.('(prefers-color-scheme: light)').matches ? LIGHT : DARK);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, isDark: theme === DARK, isExplicit, setTheme, toggleTheme, useSystemTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Theme state. Safe to call outside the provider — components rendered on
 * standalone surfaces (the public docs page) get a dark-mode default rather
 * than crashing.
 */
export function useTheme() {
  return useContext(ThemeContext) || {
    theme: DARK, isDark: true, isExplicit: false,
    setTheme: () => {}, toggleTheme: () => {}, useSystemTheme: () => {},
  };
}
